import { useMemo, useState, useEffect } from 'react';
import {
  LaunchConfig,
  LaunchConfigUpdate,
} from '@/services/launch-config.service';

export function useLaunchConfigForm(config: LaunchConfig | undefined) {
  const [editForm, setEditForm] = useState<LaunchConfigUpdate>({
    client: '',
    client_legal_name: '',
    portfolio_fund: '',
    sub_portfolio: '',
    reporting_period: '',
    valuation_date: '',
    settlement_date: '',
    mark_frequency: null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form when config changes
  useEffect(() => {
    if (!config) return;

    setEditForm({
      client: config.client || '',
      client_legal_name: config.client_legal_name || '',
      portfolio_fund: config.portfolio_fund || '',
      sub_portfolio: config.sub_portfolio || '',
      reporting_period: config.reporting_period || '',
      valuation_date: config.valuation_date || '',
      settlement_date: config.settlement_date || '',
      mark_frequency: config.mark_frequency || null,
    });
  }, [config]);

  const baseline = useMemo(
    () => ({
      client: config?.client || '',
      client_legal_name: config?.client_legal_name || '',
      portfolio_fund: config?.portfolio_fund || '',
      sub_portfolio: config?.sub_portfolio || '',
      reporting_period: config?.reporting_period || '',
      valuation_date: config?.valuation_date || '',
      settlement_date: config?.settlement_date || '',
      mark_frequency: config?.mark_frequency || null,
    }),
    [config]
  );

  const isDirty = useMemo(
    () => JSON.stringify(editForm) !== JSON.stringify(baseline),
    [editForm, baseline]
  );

  const updateField = (
    field: keyof LaunchConfigUpdate,
    value: string | null
  ) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    if (config) {
      setEditForm({
        client: config.client || '',
        client_legal_name: config.client_legal_name || '',
        portfolio_fund: config.portfolio_fund || '',
        sub_portfolio: config.sub_portfolio || '',
        reporting_period: config.reporting_period || '',
        valuation_date: config.valuation_date || '',
        settlement_date: config.settlement_date || '',
        mark_frequency: config.mark_frequency || null,
      });
    }
    setErrors({});
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    const isValidDate = (v: string) => !!v && !Number.isNaN(Date.parse(v));
    const periodOk =
      !editForm.reporting_period ||
      /^Q[1-4]\s\d{4}$/.test(editForm.reporting_period);
    const freqOk =
      !editForm.mark_frequency ||
      [
        'Daily',
        'Weekly',
        'Monthly',
        'Quarterly',
        'Semi-Annual',
        'Annual',
      ].includes(editForm.mark_frequency);

    if (!editForm.valuation_date || !isValidDate(editForm.valuation_date)) {
      next.valuation_date =
        'Valuation Date is required and must be a valid date';
    }
    if (editForm.settlement_date && !isValidDate(editForm.settlement_date)) {
      next.settlement_date = 'Settlement Date must be a valid date';
    }
    if (!periodOk) next.reporting_period = 'Use format Q# YYYY (e.g., Q1 2024)';
    if (!freqOk)
      next.mark_frequency =
        'Select one of: Daily, Weekly, Monthly, Quarterly, Semi-Annual, Annual';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const getUpdatePayload = (): LaunchConfigUpdate => {
    // For required fields, send the value (validation ensures it's not empty)
    // For optional text fields, send empty string instead of null to avoid database constraint errors
    // Empty string is safer than null when there might be related records in the database
    // This prevents 500 errors when clearing fields that have database relationships
    // We send empty string for cleared text fields so backend can handle them properly
    return {
      valuation_date: editForm.valuation_date || undefined,
      settlement_date: editForm.settlement_date?.trim() || null,
      // Use empty string instead of null for client to avoid 500 errors when clearing the field
      // Empty string allows backend to handle clearing without violating database constraints
      // This is safer than null when the field might be referenced by other records (e.g., loans)
      client: editForm.client?.trim() || '',
      client_legal_name: editForm.client_legal_name?.trim() || '',
      portfolio_fund: editForm.portfolio_fund?.trim() || '',
      sub_portfolio: editForm.sub_portfolio?.trim() || '',
      reporting_period: editForm.reporting_period?.trim() || '',
      // Explicitly include mark_frequency even if null to allow clearing the value on backend
      mark_frequency: editForm.mark_frequency ?? null,
    };
  };

  return {
    editForm,
    errors,
    isDirty,
    updateField,
    resetForm,
    validate,
    getUpdatePayload,
  };
}
